/**
 * Calendar Picker
 *
 * Date range calendar using react-day-picker with Nordex theming.
 */

import { DayPicker, type DateRange } from "react-day-picker";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import "react-day-picker/style.css";

type CalendarPickerProps = {
  /** Selected date range */
  selected: DateRange | undefined;
  /** Callback when range changes */
  onSelect: (range: DateRange | undefined) => void;
  /** Optional className */
  className?: string;
};

export function CalendarPicker({
  selected,
  onSelect,
  className,
}: CalendarPickerProps) {
  return (
    <DayPicker
      mode="range"
      selected={selected}
      onSelect={onSelect}
      showOutsideDays
      className={cn("p-0", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center pt-1 relative items-center h-7",
        caption_label: "text-sm font-medium text-foreground",
        nav: "flex items-center gap-1",
        button_previous: cn(
          "absolute left-1 top-0",
          "inline-flex items-center justify-center rounded-md",
          "h-7 w-7 bg-transparent p-0 opacity-50",
          "hover:opacity-100 hover:bg-muted transition-all",
          "text-muted-foreground"
        ),
        button_next: cn(
          "absolute right-1 top-0",
          "inline-flex items-center justify-center rounded-md",
          "h-7 w-7 bg-transparent p-0 opacity-50",
          "hover:opacity-100 hover:bg-muted transition-all",
          "text-muted-foreground"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: cn(
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
          "flex items-center justify-center h-8"
        ),
        week: "flex w-full mt-1",
        day: cn(
          "relative p-0 text-center text-sm",
          "focus-within:relative focus-within:z-20",
          "[&:has([aria-selected])]:bg-primary/10",
          "[&:has([aria-selected].day-range-end)]:rounded-r-md",
          "[&:has([aria-selected].day-range-start)]:rounded-l-md",
          "first:[&:has([aria-selected])]:rounded-l-md",
          "last:[&:has([aria-selected])]:rounded-r-md"
        ),
        day_button: cn(
          "h-8 w-8 p-0 font-normal",
          "inline-flex items-center justify-center rounded-md",
          "hover:bg-muted hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-primary/50",
          "aria-selected:opacity-100",
          "transition-colors"
        ),
        range_start: "day-range-start rounded-l-md",
        range_end: "day-range-end rounded-r-md",
        selected: cn(
          "bg-primary text-primary-foreground",
          "hover:bg-primary hover:text-primary-foreground",
          "focus:bg-primary focus:text-primary-foreground"
        ),
        today: "bg-muted text-foreground font-semibold",
        outside: "text-muted-foreground/50 aria-selected:bg-primary/30 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground/30 cursor-not-allowed",
        range_middle: cn(
          "aria-selected:bg-primary/20",
          "aria-selected:text-foreground",
          "rounded-none"
        ),
        hidden: "invisible",
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeftIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          ),
      }}
    />
  );
}

